"use client";
import {
  type AvailabilityError,
  type AvailabilityErrorCode,
  type AvailabilityErrorDetails,
  type AvailabilityErrorOfficeFutureLimitDetails,
  type AvailabilityErrorOfficeNotAvailableDetails,
  type AvailabilityErrorResourceUnavailableDetails,
  prettyTimeOnly
} from "@gettactic/api";
import { timeRelatedErrorCodes } from "@gettactic/helpers/src/reserve/timeRelatedErrorCodes";
import { getDateWindowMessage } from "@/features/reserve/utils/hourWindowMessages";
import { Alert } from "@/ui/index";

type OfficeClosedProps = {
  availabilityErrors?: AvailabilityError[];
  className?: string;
  resourceId?: string | null;
  /**
   * Render the title + messages without the `Alert` chrome (icon/background),
   * so the caller can place them inside its own container.
   */
  bare?: boolean;
  /**
   * The resource is a dedicated desk — the generic availability errors
   * ("marked as not available by an admin") are misleading, so the alert
   * explains the dedication instead, naming the owner when known.
   */
  dedicated?: { userName?: string | null } | null;
};

function AvailabilityErrorHourMessage({
  hour
}: {
  hour:
    | AvailabilityErrorOfficeNotAvailableDetails
    | AvailabilityErrorResourceUnavailableDetails;
}) {
  if (!hour) {
    return null;
  }
  const is24hours = hour.is_all_day;
  const startTime = hour.start_time ? prettyTimeOnly(hour.start_time) : "";
  const endTime = hour.end_time ? prettyTimeOnly(hour.end_time) : "";
  // A one-off holiday carries a single `date`; rules carry a start/end window.
  const startDate = hour.date ?? hour.start_date;
  const endDate = hour.date ?? hour.end_date;
  const dateWindowMsg = getDateWindowMessage(
    startDate ? `${startDate}T00:00:00Z` : null,
    endDate ? `${endDate}T00:00:00Z` : null
  );
  const hourMessage = is24hours
    ? "all day"
    : ` between ${startTime} and ${endTime}`;
  if (hour.type === "holiday") {
    return (
      <span>
        Holiday: {hour.name} - {dateWindowMsg}, {hourMessage}
      </span>
    );
  }
  if (hour.type === "weekday" && hour.weekday) {
    return (
      <span>
        On <span className="capitalize">{hour.weekday}</span>s the office is{" "}
        {hour.is_open ? "only open" : "closed"} <span>{hourMessage}</span>
      </span>
    );
  }
  if (hour.type === "rule") {
    return (
      <span>
        {dateWindowMsg}{" "}
        {!(hour as AvailabilityErrorResourceUnavailableDetails).resource_id
          ? "the office"
          : "the resource"}{" "}
        is
        {hour.is_open ? " only open" : " closed"}:{" "}
        <span className="underline">{hour.name}</span>, {hourMessage}
      </span>
    );
  }
  return <span>Unknown reason</span>;
}

const availabilityErrorMessages: Record<AvailabilityErrorCode, string> = {
  resource_assignment_not_allowed:
    "You have not been assigned to this resource.",
  resource_unavailable:
    "This resource was marked as not available by an admin.",
  resource_over_capacity:
    "This resource is reserved during the selected time. Try adjusting your time range or switching to ‘Live’ view to see what’s available",
  office_future_limit:
    "Your office admin has restricted reservations from being made this far in the future.",
  concurrent_not_allowed:
    "You already have a reservation during this period with the same resource type.",
  concurrent_not_allowed_cross_office:
    "Concurrent reservations are not allowed across offices.",
  overlaps_with_schedule:
    "This resource is reserved during the selected time. Try adjusting your time range or switching to ‘Live’ view to see what’s available.",
  buffer_overlaps_with_schedule:
    "This resource is not available during the selected time because of the resource's buffer time. Try adjusting your time range or switching to ‘Live’ view to see what’s available.",
  overlaps_with_recurring_task:
    "You already have a recurring reservation during this period.",
  office_not_available: "The office is not available during this period.",
  invalid_resource: "The resource is not available during this period."
};

function AvailabilityErrorRender({
  availabilityError: x
}: {
  availabilityError: {
    reasonCode: AvailabilityErrorCode;
    details?: AvailabilityErrorDetails;
  };
}) {
  // Handle errors that use AvailabilityErrorHourMessage component
  if (x.reasonCode === "office_not_available" && x.details) {
    const details = x.details as AvailabilityErrorOfficeNotAvailableDetails;
    return (
      <p>
        <AvailabilityErrorHourMessage hour={details} />
      </p>
    );
  }

  if (x.reasonCode === "resource_unavailable" && x.details) {
    const details = x.details as AvailabilityErrorResourceUnavailableDetails;
    return (
      <p>
        <AvailabilityErrorHourMessage hour={details} />
      </p>
    );
  }

  // Handle errors with special formatting (office_future_limit with details)
  if (x.reasonCode === "office_future_limit" && x.details) {
    const details = x.details as AvailabilityErrorOfficeFutureLimitDetails;
    return (
      <p>
        Your office admin has restricted reservations from being made this far
        in the future. Latest allowed date is {details.latest_allowed_date}.
      </p>
    );
  }

  // Handle all other errors using availabilityErrorMessages
  const message = availabilityErrorMessages[x.reasonCode];
  if (message) {
    return <p>{message}</p>;
  }

  console.error("Unrecognized error showing generic message: ", x);
  return <p>You are not allowed to reserve</p>;
}

export function RestrictionAlert({
  availabilityErrors = [],
  className = "",
  resourceId,
  bare = false,
  dedicated = null
}: OfficeClosedProps) {
  // Dedicated desks replace the whole error list: the real reason nobody
  // can book is the dedication, not the derived availability errors.
  if (dedicated) {
    const title = "This desk can't be reserved";
    const message = (
      <p>
        This is a dedicated desk
        {dedicated.userName ? ` for ${dedicated.userName}` : ""} — it can't be
        booked by anyone else.
      </p>
    );
    if (bare) {
      return (
        <div className={className}>
          <p className="font-semibold">{title}</p>
          <div className="mt-1">{message}</div>
        </div>
      );
    }
    return (
      <div className={className}>
        <Alert
          variant="tacticAlert"
          alertMessage=""
          canDismiss={false}
          alertTitle={title}
          className="mt-2 px-4 py-2"
        >
          {message}
        </Alert>
      </div>
    );
  }

  if (availabilityErrors.length === 0) {
    return <></>;
  }

  const isTimeRelatedError = availabilityErrors.some((x) =>
    timeRelatedErrorCodes.includes(x.reasonCode)
  );

  const title = resourceId
    ? `This resource can't be reserved${isTimeRelatedError ? " at the selected time" : ""}`
    : "Some of the selected days have restrictions applied";

  const messages = availabilityErrors.map((x, idx) => (
    <AvailabilityErrorRender availabilityError={x} key={idx} />
  ));

  if (bare) {
    return (
      <div className={className}>
        <p className="font-semibold">{title}</p>
        <div className="mt-1">{messages}</div>
      </div>
    );
  }

  return (
    <div className={className}>
      <Alert
        variant="tacticAlert"
        alertMessage=""
        canDismiss={false}
        alertTitle={title}
        className="mt-2 px-4 py-2"
      >
        {messages}
      </Alert>
    </div>
  );
}
